import React, { useEffect, useMemo, useState } from "react";
import { api, imgUrl, money } from "../../lib/api";
import SafeImage from "../../components/common/SafeImage";
import { toast } from "sonner";

const STATUSES = ["placed", "confirmed", "processing", "packed", "shipped", "delivered", "cancelled", "returned"];
const TABS = [
  { value: "", label: "All" },
  { value: "placed", label: "New" },
  { value: "confirmed", label: "Accepted" },
  { value: "packed", label: "Packed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
  { value: "returned", label: "Returns" },
];

export default function SellerOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/orders/seller/mine");
      setOrders(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => {
    if (!tab) return orders;
    return orders.filter((o) => o.status === tab);
  }, [orders, tab]);

  const counts = useMemo(() => {
    const c = {};
    orders.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [orders]);

  return (
    <div data-testid="seller-orders-page">
      <div className="overline mb-2">Fulfilment</div>
      <h1 className="font-display text-4xl mb-6">Your Orders</h1>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="orders-tabs">
        {TABS.map((t) => (
          <button
            key={t.value || "all"}
            onClick={() => setTab(t.value)}
            className={`chip ${tab === t.value ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`}
            data-testid={`tab-${t.value || "all"}`}
          >
            {t.label} ({t.value ? counts[t.value] || 0 : orders.length})
          </button>
        ))}
      </div>

      {error && <div className="border border-red-300 bg-red-50 text-red-800 p-3 mb-4 text-sm" data-testid="orders-error">{error}</div>}

      {loading ? (
        <div className="bg-white border border-[hsl(var(--kb-border))] p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="orders-loading">Loading orders…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[hsl(var(--kb-border))] p-8 text-center text-[hsl(var(--kb-muted))]">No orders in this tab.</div>
      ) : (
        <div className="space-y-4">
          {filtered.map((o) => (
            <div key={o.id} className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`seller-order-${o.id}`}>
              <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                <div>
                  <div className="overline">Order #{o.id.slice(0, 8)}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">{new Date(o.created_at).toLocaleString()} · {o.address?.city}, {o.address?.state}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[hsl(var(--kb-primary))] font-display text-xl">{money(o.seller_subtotal)}</div>
                  <select className="kb-input" value={o.status} onChange={(e) => update(o.id, e.target.value)} data-testid={`status-${o.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 overflow-x-auto">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex gap-2 shrink-0">
                    <SafeImage src={imgUrl(it.image)} alt="" className="w-14 aspect-[3/4] object-cover" />
                    <div className="text-xs w-40">
                      <div className="line-clamp-2">{it.title}</div>
                      <div className="text-[hsl(var(--kb-muted))]">Qty {it.qty}{it.size ? ` · ${it.size}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
